// Stage Service - API communication for application stage management
import axios from 'axios';
import { decryptText, decryptAppId } from '../utils/encryption';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/reactApi/api';

class StageService {
    
    // Update application stage
    static async updateApplicationStage(orgId, appId, stage) {
        try {
            console.log(`Updating stage for orgId: ${orgId}, appId: ${appId} to stage: ${stage}`);
            
            const response = await axios.put(
                `${API_BASE_URL}/laboratory/application-stage/${orgId}/${appId}`, 
                { stage }
            );
            
            return response.data;
        } catch (error) {
            console.error('Error updating application stage:', error);
            throw error;
        }
    }

    // Get current application stage
    static async getApplicationStage(appId) {
        try {
            const response = await axios.get(`${API_BASE_URL}/laboratory/application-stage/${appId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching application stage:', error);
            throw error;
        }
    }

    // Common stage update with predefined stages
    static async updateToSendToCAB(orgId, appId) {
        return this.updateApplicationStage(orgId, appId, '18a');
    }
    // Common stage update with predefined stages
    static async updateToSendToNABL(orgId, appId) {
        return this.updateApplicationStage(orgId, appId, '18b');
    }

    static async  updateToNoNCFound(orgId, appId) {
        return this.updateApplicationStage(orgId, appId, '19');
    }

    static async updateToUpdationComplete(orgId, appId) {
        return this.updateApplicationStage(orgId, appId, '14');
    }

    // Helper method to extract and decrypt orgId from URL
    static getOrgIdFromUrl() {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            let orgIdParam = urlParams.get('orgId');
            
            if (orgIdParam) {
                // Handle double URL encoding
                if (orgIdParam.includes('%')) {
                    orgIdParam = decodeURIComponent(orgIdParam);
                }
                console.log('🔐 StageService - Encrypted orgId from URL:', orgIdParam);
                const decrypted = decryptText(orgIdParam);
                console.log('🔓 StageService - Decrypted orgId:', decrypted);
                return decrypted;
            }
            
            return null;
        } catch (error) {
            console.error('StageService - Error decrypting orgId from URL:', error);
            return null;
        }
    }

    // Helper method to extract and decrypt appId from URL
    static getAppIdFromUrl() {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            let appIdParam = urlParams.get('appId');
            
            if (appIdParam) {
                // Handle double URL encoding
                if (appIdParam.includes('%')) {
                    appIdParam = decodeURIComponent(appIdParam);
                }
                console.log('🔐 StageService - Encrypted appId from URL:', appIdParam);
                const decrypted = decryptAppId(appIdParam);
                console.log('🔓 StageService - Decrypted appId:', decrypted);
                return decrypted;
            }
            
            return null;
        } catch (error) {
            console.error('StageService - Error decrypting appId from URL:', error);
            return null;
        }
    }

    // Helper method to get both orgId and appId from URL first, then localStorage
    static getApplicationIds() {
        // Try to get from URL parameters first (encrypted)
        let orgId = this.getOrgIdFromUrl();
        let appId = this.getAppIdFromUrl();
        
        // Fallback to localStorage if not found in URL
        if (!orgId) {
            orgId = localStorage.getItem('orgId');
            console.log('📦 StageService - Using orgId from localStorage:', orgId);
        }
        
        if (!appId) {
            appId = localStorage.getItem('appId') || orgId; // fallback to orgId if appId is not available
            console.log('📦 StageService - Using appId from localStorage:', appId);
        }
        
        console.log('✅ StageService - Final IDs:', { orgId, appId });
        
        return { orgId, appId };
    }

    // Common update method that handles both button actions
    static async updateStageForAction(action) {
        const { orgId, appId } = this.getApplicationIds();
        
        if (!orgId || !appId) {
            throw new Error('Organization ID or Application ID not found in localStorage');
        }

        switch (action) {
            case 'SEND_TO_CAB':
                return this.updateToSendToCAB(orgId, appId);
            case 'NO_NC_FOUND':
                return this.updateToNoNCFound(orgId, appId);
            case 'SEND_TO_NABL':
                return this.updateToSendToNABL(orgId, appId);
            case 'UPDATATION_COMPLETE':
                return this.updateToUpdationComplete(orgId, appId);
            default:
                throw new Error(`Unknown action: ${action}`);
        }
    }
}

export default StageService;